/**
 * Trading Analysis Page
 *
 * Main page component for the Trading Analysis feature.
 */

import { useState, useEffect, useMemo } from "react";
import { AuthGate } from "@/components/auth/auth-gate";
import { Spinner } from "@/components/spinner";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { useTradeHistory } from "@/backend/hooks/use-trade-history";
import { TradingAnalysisProvider } from "./trading-analysis-context";
import { type DateRangeValue } from "@/components/ui/date-range-selector";
import { getMockTradingAnalysisFills } from "@/lib/mock-data";
import {
  AnalysisHeaderCard,
  type AnalysisTab,
  OverviewTab,
  PerformanceTab,
  CalendarTab,
  TimeAnalysisTab,
  AssetAnalysisTab,
  BehaviorTab,
  AchievementsTab,
  ReviewTab,
} from "./components";

export function TradingAnalysisPage() {
  const [activeTab, setActiveTab] = useState<AnalysisTab>("overview");
  const [dateRange, setDateRange] = useState<DateRangeValue>("all");
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const { isAuthenticated } = useUnifiedAuth();

  // Scroll detection for sticky header
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      setIsHeaderSticky(scrollTop > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Get mock fills for preview when not authenticated
  const mockFills = useMemo(() => {
    if (isAuthenticated) return [];
    return getMockTradingAnalysisFills();
  }, [isAuthenticated]);

  return (
    <AuthGate
      title="Performance Lab Pro™"
      description="Connect to view your advanced trading analytics, patterns, and achievements."
    >
      <TradingAnalysisPageContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        dateRange={dateRange}
        setDateRange={setDateRange}
        isHeaderSticky={isHeaderSticky}
        previewFills={!isAuthenticated ? mockFills : undefined}
      />
    </AuthGate>
  );
}

interface TradingAnalysisPageContentProps {
  activeTab: AnalysisTab;
  setActiveTab: (tab: AnalysisTab) => void;
  dateRange: DateRangeValue;
  setDateRange: (range: DateRangeValue) => void;
  isHeaderSticky: boolean;
  previewFills?: ReturnType<typeof getMockTradingAnalysisFills>;
}

function TradingAnalysisPageContent({
  activeTab,
  setActiveTab,
  dateRange,
  setDateRange,
  isHeaderSticky,
  previewFills,
}: TradingAnalysisPageContentProps) {
  const { userId, isReady } = useUnifiedAuth();
  const { data: fills = [], isLoading, isFetching } = useTradeHistory(userId ?? undefined);

  // Use preview fills for unauthenticated users, real fills for authenticated
  const effectiveFills = previewFills ?? fills;
  const effectiveLoading = previewFills ? false : isLoading;

  // Show loading if:
  // 1. Auth is not ready (prevents race condition during authentication)
  // 2. Initial data is loading
  // 3. Data is refetching AND we have no data yet (prevents empty state flash)
  if (!isReady || effectiveLoading || (isFetching && fills.length === 0 && !previewFills)) {
    return (
      <main className="flex-1 p-4 md:p-6">
        <div className="max-w-[1600px] mx-auto flex items-center justify-center h-96">
          <Spinner className="size-8" />
        </div>
      </main>
    );
  }

  return (
    <TradingAnalysisProvider
      fills={effectiveFills}
      isLoading={effectiveLoading}
      dateRange={dateRange}
    >
      <main className="flex-1 p-4 md:p-6">
        <div className="max-w-[1600px] mx-auto space-y-4">
          {/* Header Card with Tabs and Date Range */}
          <AnalysisHeaderCard
            activeTab={activeTab}
            onTabChange={setActiveTab}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            isSticky={isHeaderSticky}
          />

          {/* Tab Content */}
          {activeTab === "overview" && <OverviewTab />}
          {activeTab === "performance" && <PerformanceTab />}
          {activeTab === "calendar" && <CalendarTab />}
          {activeTab === "time" && <TimeAnalysisTab />}
          {activeTab === "assets" && <AssetAnalysisTab />}
          {activeTab === "behavior" && <BehaviorTab />}
          {activeTab === "achievements" && <AchievementsTab />}
          {activeTab === "review" && <ReviewTab />}
        </div>
      </main>
    </TradingAnalysisProvider>
  );
}

